import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { AlertsType } from '@rappi/ui/alerts';

import { PurchaseOrderFormConfigService } from '../../services/purchase-order-form-config.service';
import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import {
  CatalogItem,
  FinancesConfig,
  NewPurchaseOrderConfig,
  PurchaseOrder,
  PurchaseOrderCreateRequest,
  PurchaseOrderDetail,
  PurchaseOrderUpdateRequest,
} from '../../models/makers-finances-mgr.models';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { HasPermissionsService } from '../../../shared/services/has-permissions/has-permissions.service';
import {
  DETAIL_MODE, DetailMode,
  SOURCE_TYPE, SourceType,
  BUSINESS_UNIT,
  PO_FIELD, PO_SECTION, PO_PERMISSIONS,
  PO_STATUS, PO_STATUS_LABELS, PO_SUCCESS_MESSAGE,
  PO_ERROR_MESSAGE, PO_QUERY_PARAM, PO_AGENCY_CLEARED_ID,
  PO_SAVE_ERROR_FALLBACK, PO_ERROR_ALERT_DURATION_MS, PO_HTTP_STATUS,
} from './purchase-order.constants';
import { UploadedFileValue } from '../shared/files-card/files-card.types';
import { PoConfirmDialogComponent } from './po-confirm-dialog/po-confirm-dialog.component';
import { PoExternalIdConflictDialogComponent } from './po-external-id-conflict-dialog/po-external-id-conflict-dialog.component';

export interface FieldDef { key: string; cfg: any }
export interface SectionDef { key: string; cfg: any; fields: FieldDef[] }
export { DetailMode, SourceType };

@Component({
  selector: 'app-purchase-order-detail',
  templateUrl: './purchase-order-detail.component.html',
  styleUrls: ['./purchase-order-detail.component.scss']
})
export class PurchaseOrderDetailComponent implements OnInit, OnDestroy {
  mode: DetailMode = DETAIL_MODE.CREATE;
  sourceType: SourceType = SOURCE_TYPE.NONE;
  lockedSourceType: SourceType | null = null;
  form: FormGroup;
  configLoaded = false;
  catalogsLoaded = false;
  formSubmitted = false;
  isSubmitting = false;
  remoteConfig: NewPurchaseOrderConfig | null = null;
  // Full CMS config — the document section reads its top-level keys (po_document_max_count /
  // po_document_max_size_mb), which are siblings of new_purchase_order.
  financesConfig: FinancesConfig | null = null;
  // CMS rollout flag (FinancesConfig.po_business_unit_enabled, set per country). While off, the
  // Business Unit field/selector swap is hidden and Submit never requires it (legacy behavior).
  poBusinessUnitEnabled = false;
  // CMS rollout flag (FinancesConfig.budget_type_validation_enabled, set per country). While off,
  // Budget type is hidden (even for Partnerships) and never required on Submit (legacy behavior).
  budgetTypeValidationEnabled = false;
  // CMS rollout flag (FinancesConfig.trato_expiration_date_enabled, set per country). While off,
  // Expiration date is hidden on create and never required (legacy: backend calculates it at Submit).
  tratoExpirationDateEnabled = false;
  sourceTypeCatalog: CatalogItem[] = [];
  poTypeCatalog: CatalogItem[] = [];
  tagsCatalog: CatalogItem[] = [];

  // Edit-mode state. poId is the route :id; poDetail holds the loaded PO; poLoaded flips true once
  // the form has been patched, signalling the section children to call setSelection on their selects
  // (one-select is not form-bound, so patching the control alone doesn't update its UI).
  poId: number | null = null;
  poDetail: PurchaseOrderDetail | null = null;
  poLoaded = false;
  // When the edit route is reached from the list (?from=list), Cancel/Save-success return to the
  // list instead of the read-only detail view.
  cameFromList = false;
  // Owner email validation state — false blocks save/submit buttons.
  ownerValid = true;
  // True when the user explicitly cleared the agency via the "None" affordance. On updates of an
  // existing PO this makes _buildCreateRequest send agency_id: 0 instead of omitting the field.
  agencyExplicitlyCleared = false;
  private formPatched = false;

  get primaryLabel(): string {
    if (!this.config) {
      return 'Submit for approval';
    }
    const labels = this.config.footer_actions.primary_action.labels as Record<string, string>;
    return labels[this.sourceType] || labels['default'];
  }

  // When editing a Draft PO (e.g. reached from the list via /edit?from=list), the footer shows the
  // creation buttons (Save draft + Submit for approval) instead of the edit buttons (Cancel + Save
  // changes). The difference is that Save draft/Submit call updatePurchaseOrder (the PO already has
  // an id) rather than createPurchaseOrder.
  get isDraftEdit(): boolean {
    return this.mode === DETAIL_MODE.EDIT && this.poDetail?.stage === PO_STATUS_LABELS[PO_STATUS.DRAFT];
  }

  get saveDraftEnabled(): boolean {
    return !!(this.form?.get(PO_FIELD.PURCHASE_ORDER_NAME)?.value?.trim()) && !!this.sourceType && this.ownerValid;
  }

  get primaryEnabled(): boolean {
    return this.saveDraftEnabled && !!this.sourceType;
  }

  readonly SECTION = PO_SECTION;
  readonly IS_TEST_FIELD = PO_FIELD.IS_TEST;

  // PO test checkbox is disabled once the PO has an id (edit or draft-edit mode)
  get isTestDisabled(): boolean {
    return !!this.poId;
  }

  get isAdmin(): boolean {
    return this.permissionsService.isAdmin();
  }

  sectionFieldConfigs: Record<string, Record<string, any>> = {};
  sectionCfgs: Record<string, any> = {};

  private config: any;
  private sections: SectionDef[] = [];
  private readonly destroy$ = new Subject<void>();
  private readonly _sourceTypeIdMap = new Map<string, number>();
  private readonly _poTypeIdMap = new Map<string, number>();
  private readonly _tagIdMap = new Map<string, number>();
  // Reverse maps (id → code) used to map the loaded PO's ids back to the catalog codes the form
  // stores as control values.
  private readonly _sourceTypeCodeMap = new Map<number, string>();
  private readonly _poTypeCodeMap = new Map<number, string>();
  private readonly _tagCodeMap = new Map<number, string>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly configService: PurchaseOrderFormConfigService,
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly alerts: AlertsService,
    private readonly permissionsService: HasPermissionsService,
  ) {}

  ngOnInit(): void {
    this.mode = this.route.snapshot.data['mode'] || DETAIL_MODE.CREATE;
    this._initLockedSourceType();

    // Edit mode: capture the route id and fetch the existing PO. The form is patched once config,
    // catalogs and the PO are all loaded (see _tryPatchForm).
    if (this.mode === DETAIL_MODE.EDIT) {
      const idParam = this.route.snapshot.paramMap.get('id');
      const id = Number(idParam);
      if (!idParam || !Number.isFinite(id)) {
        this.router.navigate(['/financial-hub/purchase-orders']);
        return;
      }
      this.poId = id;
      this.cameFromList = this.route.snapshot.queryParamMap.get('from') === 'list';
      this.financesService.getPurchaseOrder(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.poDetail = res.data;
            this._tryPatchForm();
          },
          error: () => {
            alert('Could not load the purchase order. Please try again.');
            this.router.navigate(['/financial-hub/purchase-orders']);
          },
        });
    }

    this.configService.getConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe(config => {
        this.config = config;
        this.buildSections();
        this.buildForm();
        this.configLoaded = true;
        this._tryPatchForm();

        this.form.get(PO_FIELD.SOURCE_TYPE)?.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((val: SourceType) => {
            this._clearNonEditableForSource(val);
            this.sourceType = val;
            this.clearNonApplicableFields();
            this._updateMandatoryValidators(val);
          });
      });

    this.financesService.config$
      .pipe(filter(c => !!c), take(1), takeUntil(this.destroy$))
      .subscribe(remoteConfig => {
        this.remoteConfig = remoteConfig.new_purchase_order;
        this.financesConfig = remoteConfig;
        this.poBusinessUnitEnabled = !!remoteConfig.po_business_unit_enabled;
        this.budgetTypeValidationEnabled = !!remoteConfig.budget_type_validation_enabled;
        this.tratoExpirationDateEnabled = !!remoteConfig.trato_expiration_date_enabled;
        // Reconcile immediately: the flag can resolve after source_type is already Trato (e.g. edit
        // mode's silent patch, or the user picking Trato before the flag loads). A no-op if config
        // hasn't finished loading yet (this.sections is still empty).
        this._updateMandatoryValidators(this.sourceType);
      });

    forkJoin({
      sourceTypes: this.financesService.getSourceTypes(),
      tagOptions: this.financesService.getTagOptions(),
      poTypeOptions: this.financesService.getPoTypeOptions(),
    }).pipe(takeUntil(this.destroy$))
      .subscribe(({ sourceTypes, tagOptions, poTypeOptions }) => {
        this.sourceTypeCatalog = sourceTypes.data.records;
        this.tagsCatalog = tagOptions.data.records;
        this.poTypeCatalog = poTypeOptions.data.records;

        this.sourceTypeCatalog.forEach(r => {
          this._sourceTypeIdMap.set(r.code, r.id);
          this._sourceTypeCodeMap.set(r.id, r.code);
        });
        this.tagsCatalog.forEach(r => {
          this._tagIdMap.set(r.code, r.id);
          this._tagCodeMap.set(r.id, r.code);
        });
        this.poTypeCatalog.forEach(r => {
          this._poTypeIdMap.set(r.code, r.id);
          this._poTypeCodeMap.set(r.id, r.code);
        });
        this.catalogsLoaded = true;
        this._tryPatchForm();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveDraft(): void {
    if (!this.saveDraftEnabled || this.isSubmitting) {
      return;
    }
    this._openConfirmDialog({
      title: 'Save as draft?',
      message: 'Your purchase order will be saved as a draft. You can continue editing it later.',
      confirmLabel: 'Yes, save draft',
      isSubmit: false,
    });
  }

  submitForm(): void {
    if (!this.primaryEnabled || !this.config || this.isSubmitting) {
      return;
    }
    this.formSubmitted = true;

    console.log('[submitForm] form valid:', this.form.valid);
    console.log('[submitForm] form value:', this.form.value);
    console.log('[submitForm] form errors:', this._collectFormErrors());

    const clickValidations = this.config.footer_actions.primary_action.validations_on_click as Record<SourceType, string[]>;
    // business_unit is only required on Submit while the CMS flag is on (see
    // makers-finances-mgr/docs/plan/business-unit-frontend-integration-guide.md §2/§5) — while off,
    // the field is hidden (section-general-info's isBusinessUnitVisible) and must never block Submit.
    const toValidate: string[] = (clickValidations[this.sourceType] ?? [])
      .filter(key => key !== PO_FIELD.BUSINESS_UNIT || this.poBusinessUnitEnabled);
    const isRetailerAds = this.poBusinessUnitEnabled
      && this.form.get(PO_FIELD.BUSINESS_UNIT)?.value === BUSINESS_UNIT.RETAILER_ADS;
    let hasErrors = false;
    for (const rawKey of toValidate) {
      // maker_to_execute is only the active selector for Maker Ads; swap it for the Brand Group
      // field when the PO is Retailer Ads so Submit validates whichever selector is actually visible.
      const key = rawKey === PO_FIELD.MAKER_TO_EXECUTE && isRetailerAds
        ? PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID
        : rawKey;
      const ctrl = this.form.get(key);
      if (ctrl && !ctrl.value) {
        ctrl.markAsTouched();
        hasErrors = true;
        console.log(`[submitForm] click validation failed for "${key}" — value:`, ctrl.value);
      }
    }
    // Validate country-conditional required fields set reactively by section components, plus any
    // other control error (e.g. the PO External ID availability conflict set on PO_NUMBER).
    this.form.markAllAsTouched();
    for (const key of Object.keys(this.form.controls)) {
      const ctrlErrors = this.form.get(key)?.errors;
      if (ctrlErrors) {
        hasErrors = true;
        console.log(`[submitForm] control "${key}" has errors:`, ctrlErrors);
      }
    }
    console.log('[submitForm] hasErrors:', hasErrors);
    if (hasErrors) {
      return;
    }
    this._openConfirmDialog({
      title: 'Submit form?',
      message: 'Please check the information, changes are not allowed until approval.',
      confirmLabel: 'Yes, send request',
      isSubmit: true,
    });
  }

  // Edit mode: validate the form and PUT the changes (no submit step). Mirrors submitForm's error
  // check but skips the create-only click validations and the submit dialog.
  saveChanges(): void {
    if (!this.primaryEnabled || this.isSubmitting) {
      return;
    }
    this.formSubmitted = true;
    this.form.markAllAsTouched();

    console.log('[saveChanges] form valid:', this.form.valid);
    console.log('[saveChanges] form value:', this.form.value);
    console.log('[saveChanges] form errors:', this._collectFormErrors());

    let hasErrors = false;
    for (const key of Object.keys(this.form.controls)) {
      if (this.form.get(key)?.errors) {
        hasErrors = true;
        break;
      }
    }
    console.log('[saveChanges] hasErrors:', hasErrors);
    if (hasErrors) {
      return;
    }
    this._maybeShowExternalIdConflict().pipe(take(1)).subscribe(proceed => {
      if (!proceed) {
        return;
      }
      this._callUpdateService();
    });
  }

  private _callUpdateService(isSubmit = false): void {
    if (this.poId == null) {
      return;
    }
    this.isSubmitting = true;
    const request$ = this.financesService.updatePurchaseOrder(
      this.poId,
      this._buildCreateRequest() as PurchaseOrderUpdateRequest,
    );
    this._persistAndNavigate(request$, isSubmit, () => this._editExitRoute());
  }

  // Shared handling for create/update: optionally chains the submit call, then surfaces the
  // non-blocking duplicate_external_id warning (PR #38) before navigating to the success route.
  private _persistAndNavigate(
    request$: Observable<{ message: string; data: PurchaseOrder }>,
    isSubmit: boolean,
    successRoute: () => any[],
  ): void {
    request$
      .pipe(
        switchMap(res => {
          // Store the PO ID so a retry after a failed submit updates instead of creating a duplicate.
          if (this.poId == null) {
            this.poId = res.data.id;
          }
          return isSubmit
            ? this.financesService.submitPurchaseOrder(res.data.id).pipe(map(() => res))
            : of(res);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.alerts.openAlerts(this._successMessage(isSubmit), AlertsType.success);
          this.router.navigate(successRoute());
        },
        error: (err) => {
          this.isSubmitting = false;
          this._showCreateError(err);
        },
      });
  }

  cancelEdit(): void {
    this.router.navigate(this._editExitRoute(), {
      queryParams: { [PO_QUERY_PARAM.COUNTRY]: this.storeAux.country },
    });
  }

  private _successMessage(isSubmit: boolean): string {
    if (isSubmit) {
      return PO_SUCCESS_MESSAGE.SUBMIT;
    }
    if (this.mode === DETAIL_MODE.EDIT && !this.isDraftEdit) {
      return PO_SUCCESS_MESSAGE.UPDATE;
    }
    return PO_SUCCESS_MESSAGE.DRAFT;
  }

  // Shows the backend `message` as-is (create/update/submit errors). Known codes are mapped first;
  // 5xx and responses without a message fall back to a generic text.
  private _showCreateError(err: any): void {
    const serverMessage = err?.error?.message;
    const hasServerMessage = typeof serverMessage === 'string' && serverMessage.trim() !== '';
    const message = PO_ERROR_MESSAGE[serverMessage as keyof typeof PO_ERROR_MESSAGE]
      ?? (hasServerMessage && err?.status !== PO_HTTP_STATUS.INTERNAL_SERVER_ERROR ? serverMessage : PO_SAVE_ERROR_FALLBACK);
    this.alerts.openAlerts(message, AlertsType.error, {
      ...this.alerts.configAlerts,
      duration: PO_ERROR_ALERT_DURATION_MS,
    });
  }

  // Where Cancel / save-success go after editing: back to the list when reached from the list
  // (?from=list), otherwise to the read-only detail view.
  private _editExitRoute(): any[] {
    return this.cameFromList
      ? ['/financial-hub/purchase-orders']
      : ['/financial-hub/purchase-orders', this.poId];
  }

  private _collectFormErrors(): Record<string, any> {
    const errors: Record<string, any> = {};
    for (const key of Object.keys(this.form.controls)) {
      const ctrlErrors = this.form.get(key)?.errors;
      if (ctrlErrors) {
        errors[key] = ctrlErrors;
      }
    }
    return errors;
  }

  // Pre-submit check: if the external ID is in use by another PO, show a warning modal with
  // "Go back" / "Submit anyway". Returns true when the user can proceed (no conflict or "Submit
  // anyway"), false when the user chose "Go back" or closed the modal.
  private _maybeShowExternalIdConflict(): Observable<boolean> {
    const externalId = this.form.get(PO_FIELD.PO_NUMBER)?.value;
    const companyId = this.form.get(PO_FIELD.COMPANY_TO_BILL)?.value;
    if (!externalId || !companyId) {
      return of(true);
    }
    return this.financesService.checkExternalIdAvailability(
      externalId, companyId, this.storeAux.country, this.poId ?? undefined,
    ).pipe(
      catchError(() => of(null)),
      switchMap(res => {
        if (res?.data && !res.data.available) {
          const message = res.data.warning
            || (res.data.conflicting_po_id != null
              ? `There is another PO (${res.data.conflicting_po_id}) using the same External ID`
              : 'External ID is already in use');
          const ref = this.dialog.open(PoExternalIdConflictDialogComponent, {
            width: '460px',
            panelClass: 'poc-dialog-panel',
            data: { message },
          });
          return ref.afterClosed().pipe(map(result => result === true));
        }
        return of(true);
      }),
    );
  }

  private _openConfirmDialog(opts: { title: string; message: string; confirmLabel: string; isSubmit: boolean }): void {
    this._maybeShowExternalIdConflict().pipe(take(1)).subscribe(proceed => {
      if (!proceed) {
        return;
      }
      const ref = this.dialog.open(PoConfirmDialogComponent, {
        width: '460px',
        panelClass: 'poc-dialog-panel',
        data: { title: opts.title, message: opts.message, confirmLabel: opts.confirmLabel },
      });
      ref.afterClosed().pipe(take(1)).subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        if (this.isDraftEdit) {
          this._callUpdateService(opts.isSubmit);
        } else {
          this._callCreateService(opts.isSubmit);
        }
      });
    });
  }

  private _callCreateService(isSubmit: boolean): void {
    this.isSubmitting = true;
    // If the PO was already created (e.g. create succeeded but submit failed on a previous attempt),
    // update it instead of creating a duplicate.
    const request$ = this.poId != null
      ? this.financesService.updatePurchaseOrder(this.poId, this._buildCreateRequest() as PurchaseOrderUpdateRequest)
      : this.financesService.createPurchaseOrder(this._buildCreateRequest());
    this._persistAndNavigate(request$, isSubmit, () => ['/financial-hub/purchase-orders']);
  }

  private _buildCreateRequest(): PurchaseOrderCreateRequest {
    const v = this.form.getRawValue();
    const docs = (v[PO_FIELD.DOCUMENT_FILE] as UploadedFileValue[] | null) ?? [];
    const documents = docs.map(file => ({
      ...(file.id != null ? { id: file.id } : {}),
      key: file.key,
      file_name: file.name,
      uploaded_at: file.uploadedAt,
    }));

    const tagCodes: string[] = v[PO_FIELD.TAGS] ?? [];
    const tagIds = tagCodes
      .map(code => this._tagIdMap.get(code))
      .filter((id): id is number => id !== undefined);

    const businessUnit = this._resolveBusinessUnit(v);

    const req: PurchaseOrderCreateRequest = {
      name:                      v[PO_FIELD.PURCHASE_ORDER_NAME],
      country:                   this.financesService.resolveCountry(this.storeAux.country),
      source_type_id:            this._sourceTypeIdMap.get(v[PO_FIELD.SOURCE_TYPE]),
      po_type_id:                this._poTypeIdMap.get(v[PO_FIELD.PO_TYPE]),
      owner_id:                  v[PO_FIELD.OWNER]               || undefined,
      business_unit:             businessUnit                    || undefined,
      ...this._buildExecutionFields(v, businessUnit),
      is_test:                   v[PO_FIELD.IS_TEST]             ?? false,
      parent_po_id:              v[PO_FIELD.PARENT_PO]           ? parseInt(v[PO_FIELD.PARENT_PO], 10)       : undefined,
      self_service_campaign_id:  v[PO_FIELD.SELF_SERVICE_CAMPAIGN_ID] || undefined,
      currency:                  v[PO_FIELD.PO_CURRENCY]         || undefined,
      po_budget:                 v[PO_FIELD.PO_BUDGET]           != null ? parseFloat(v[PO_FIELD.PO_BUDGET]) : undefined,
      co_investment_percentage:  v[PO_FIELD.CO_INVESTMENT]       != null ? parseFloat(v[PO_FIELD.CO_INVESTMENT]) : undefined,
      re_investment_percentage:  v[PO_FIELD.RE_INVESTMENT]       != null ? parseFloat(v[PO_FIELD.RE_INVESTMENT]) : undefined,
      external_id:               v[PO_FIELD.PO_NUMBER]           || undefined,
      // Zoho IDs (company/contact/agency) are 19-digit int64 values that exceed
      // Number.MAX_SAFE_INTEGER; parseInt would silently truncate them and the backend would
      // then 404 on the rounded ID. Keep them as strings and serialize as raw numeric literals
      // (see MakersFinancesMgrService._stringifyBody).
      company_to_bill_id:        v[PO_FIELD.COMPANY_TO_BILL]     || undefined,
      contact_id:                v[PO_FIELD.POINT_OF_CONTACT]    || undefined,
      agency_id:                 this._resolveAgencyId(v),
      revenue_recognition_method: this.storeAux.country === 'CO' ? v[PO_FIELD.AGENCY_RECOGNITION_METHOD] || undefined : undefined,
      tax_percentage:            this.storeAux.country === 'BR' && v[PO_FIELD.TAX_PERCENTAGE] != null ? v[PO_FIELD.TAX_PERCENTAGE] : undefined,
      // Gated by the budget_type_validation_enabled CMS flag (guide §3.5) — while off, the field is
      // hidden (SectionBudgetDatesComponent's isBudgetTypeVisible) and must never leak into the
      // payload even from a stale form value. Only applies to Partnerships POs; the control is
      // already null otherwise (see SectionBudgetDatesComponent's po_type subscription), and the
      // backend auto-nulls budget_type_id server-side whenever po_type isn't Partnerships (§3.2), so
      // omitting it here on Update is safe.
      budget_type:               this.budgetTypeValidationEnabled ? (v[PO_FIELD.BUDGET_TYPE] || undefined) : undefined,
      expiration_date:           this._toDateOnlyString(v[PO_FIELD.EXPIRATION_DATE]),
      request_reason:            v[PO_FIELD.REQUEST_REASON]      || undefined,
      tag_ids:                   tagIds.length                   ? tagIds                                     : undefined,
      additional_info:           v[PO_FIELD.DETAIL_BRIEF]        || undefined,
      // Full-state array: ids only for docs that already exist (update reconciles by id —
      // absent ids are deleted, id-less items are created). On UPDATE the array is always
      // sent, even empty — an explicit [] is the only way to express "delete every document"
      // under the full-state contract; omitting the key could read as "leave unchanged".
      // CREATE keeps the old omit-when-empty behavior.
      documents:                 this.poId != null                  ? documents
                                 : (documents.length               ? documents : undefined),
    };

    return Object.fromEntries(
      Object.entries(req).filter(([, val]) => val !== undefined)
    ) as PurchaseOrderCreateRequest;
  }

  // While the CMS flag is off, Business Unit doesn't exist for this PO — never resolve a value even
  // if a stale one is sitting in the (hidden) form control.
  private _resolveBusinessUnit(v: any): typeof BUSINESS_UNIT[keyof typeof BUSINESS_UNIT] | null {
    return this.poBusinessUnitEnabled
      ? (v[PO_FIELD.BUSINESS_UNIT] as typeof BUSINESS_UNIT[keyof typeof BUSINESS_UNIT] | null)
      : null;
  }

  // Only one execution selector may be sent per Business Unit — the backend rejects the request if
  // both (or the wrong one) are present. Gate on businessUnit here, not just on the form's current
  // visibility state, so a stale/disabled control value never leaks into the payload.
  private _buildExecutionFields(v: any, businessUnit: string | null): Pick<PurchaseOrderCreateRequest,
    'maker_code' | 'maker_name' | 'brand_group_to_execute_id' | 'brand_group_to_execute_name'> {
    if (businessUnit === BUSINESS_UNIT.RETAILER_ADS) {
      return {
        maker_code: undefined,
        maker_name: undefined,
        brand_group_to_execute_id: v[PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID]
          ? parseInt(v[PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID], 10) : undefined,
        brand_group_to_execute_name: v[PO_FIELD.BRAND_GROUP_TO_EXECUTE_NAME] || undefined,
      };
    }
    return {
      maker_code: v[PO_FIELD.MAKER_TO_EXECUTE] || undefined,
      maker_name: v[PO_FIELD.MAKER_NAME] || undefined,
      brand_group_to_execute_id: undefined,
      brand_group_to_execute_name: undefined,
    };
  }

  // On an existing PO, an explicit "None" (agency cleared) is sent as the literal number 0 so the
  // backend removes the agency; otherwise the field is omitted when no agency is set. New POs never
  // send 0. Unlike the other Zoho ids here (kept as strings to avoid int64 precision loss), 0 is a
  // small sentinel value, not a real Zoho id, so it's sent as a plain number — see agency_id's type
  // in PurchaseOrderCreateRequest.
  private _resolveAgencyId(v: any): string | number | undefined {
    if (this.agencyExplicitlyCleared && this.poId != null) {
      return PO_AGENCY_CLEARED_ID;
    }
    return v[PO_FIELD.AGENCY] || undefined;
  }

  // Edit mode: patch the form exactly once, after config (form built), catalogs (reverse maps
  // populated) and the PO are all loaded. Each of the three async paths calls this.
  private _tryPatchForm(): void {
    if (this.formPatched) {
      return;
    }
    if (!this.configLoaded || !this.catalogsLoaded || !this.poDetail) {
      return;
    }
    this.formPatched = true;
    this._patchFormFromDetail(this.poDetail);
  }

  // Patch the form from the loaded PO. Order matters: SOURCE_TYPE is patched with emitEvent:false
  // so its valueChanges subscription (which clears non-editable/non-applicable fields) does NOT
  // fire and wipe the values we are about to set. We then set this.sourceType + validators
  // manually, patch the rest silently, and finally flip poLoaded so section selects call
  // setSelection (one-select is not form-bound).
  private _patchFormFromDetail(detail: PurchaseOrderDetail): void {
    const patch = this._buildFormPatch(detail);

    this.form.get(PO_FIELD.SOURCE_TYPE)?.setValue(patch[PO_FIELD.SOURCE_TYPE] ?? null, { emitEvent: false });
    this.sourceType = (patch[PO_FIELD.SOURCE_TYPE] as SourceType) ?? SOURCE_TYPE.NONE;
    this._clearNonEditableForSource(this.sourceType);
    this._updateMandatoryValidators(this.sourceType);

    for (const [key, value] of Object.entries(patch)) {
      if (key === PO_FIELD.SOURCE_TYPE) {
        continue;
      }
      this.form.get(key)?.setValue(value ?? null, { emitEvent: false });
    }

    this.poLoaded = true;
  }

  // Maps each PurchaseOrderDetail field to its PO_FIELD form control value. Catalog ids are
  // resolved to codes via the reverse maps; numeric/percentage fields are stringified because the
  // form controls hold strings (parsed back to numbers in _buildCreateRequest).
  private _buildFormPatch(detail: PurchaseOrderDetail): Record<string, any> {
    const patch: Record<string, any> = {};

    // Fields copied as-is when present.
    const directFields: Array<[string, any]> = [
      [PO_FIELD.PURCHASE_ORDER_NAME, detail.name],
      [PO_FIELD.MAKER_TO_EXECUTE, detail.maker_code],
      [PO_FIELD.MAKER_NAME, detail.maker_name],
      [PO_FIELD.BUSINESS_UNIT, detail.business_unit],
      [PO_FIELD.BRAND_GROUP_TO_EXECUTE_NAME, detail.brand_group_to_execute_name],
      [PO_FIELD.BUDGET_TYPE, detail.budget_type],
      [PO_FIELD.OWNER, detail.owner_id],
      [PO_FIELD.PO_CURRENCY, detail.currency],
      [PO_FIELD.PO_NUMBER, detail.external_id],
      [PO_FIELD.COMPANY_TO_BILL, detail.company_to_bill_id],
      [PO_FIELD.POINT_OF_CONTACT, detail.contact_id],
      [PO_FIELD.AGENCY, detail.agency_id],
      [PO_FIELD.AGENCY_RECOGNITION_METHOD, detail.revenue_recognition_method],
      [PO_FIELD.REQUEST_REASON, detail.request_reason],
      [PO_FIELD.SELF_SERVICE_CAMPAIGN_ID, detail.self_service_campaign_id],
      [PO_FIELD.DETAIL_BRIEF, detail.additional_info],
      [PO_FIELD.IS_TEST, detail.is_test],
    ];
    for (const [field, value] of directFields) {
      if (value != null) {
        patch[field] = value;
      }
    }

    // Numeric/percentage fields stringified when present (the form controls hold strings).
    type FieldValue = number | string;
    const stringFields: Array<[string, FieldValue | null | undefined]> = [
      [PO_FIELD.PARENT_PO, detail.parent_po_id],
      [PO_FIELD.BRAND_GROUP_TO_EXECUTE_ID, detail.brand_group_to_execute_id],
      [PO_FIELD.PO_BUDGET, detail.budget],
      [PO_FIELD.CO_INVESTMENT, detail.co_investment_percentage],
      [PO_FIELD.RE_INVESTMENT, detail.re_investment_percentage],
      [PO_FIELD.TAX_PERCENTAGE, detail.tax_percentage],
    ];
    for (const [field, value] of stringFields) {
      if (value != null) {
        patch[field] = String(value);
      }
    }

    // Catalog ids resolved to codes via the reverse maps (kept null when unmapped).
    if (detail.source_type_id != null) {
      patch[PO_FIELD.SOURCE_TYPE] = this._sourceTypeCodeMap.get(detail.source_type_id) ?? null;
    }
    if (detail.po_type_id != null) {
      patch[PO_FIELD.PO_TYPE] = this._poTypeCodeMap.get(detail.po_type_id) ?? null;
    }

    // Converted (not a directField) — the datepicker control expects a Date, not the backend's
    // date-only string. See _parseDateOnly.
    if (detail.expiration_date) {
      patch[PO_FIELD.EXPIRATION_DATE] = this._parseDateOnly(detail.expiration_date);
    }

    const tagCodes = this._resolveTagCodes(detail.tags);
    if (tagCodes) {
      patch[PO_FIELD.TAGS] = tagCodes;
    }

    const docs = this._buildDocValues(detail);
    patch[PO_FIELD.DOCUMENT_FILE] = docs;

    return patch;
  }

  private _resolveTagCodes(tags: PurchaseOrderDetail['tags']): string[] | null {
    if (!tags?.length) {
      return null;
    }
    const codes = tags.map(t => this._tagCodeMap.get(t.id)).filter((c): c is string => !!c);
    return codes.length ? codes : null;
  }

  // The detail's documents array becomes the form control's UploadedFileValue[] — ids included
  // so the update PUT can reconcile (kept docs carry their id; removed docs drop out of the
  // array and the backend deletes them).
  private _buildDocValues(detail: PurchaseOrderDetail): UploadedFileValue[] {
    return (detail.documents ?? []).map(doc => ({
      id: doc.id,
      key: doc.key,
      name: doc.file_name,
      uploadedAt: doc.uploaded_at,
    }));
  }

  goBack(): void {
    this.router.navigate(['/financial-hub/purchase-orders']);
  }

  onOwnerValidChange(valid: boolean): void {
    this.ownerValid = valid;
  }

  isSectionVisibleForSource(sectionKey: string): boolean {
    const cfg = this.sectionCfgs[sectionKey];
    if (!cfg) {
      return false;
    }
    if (cfg.visible === true) {
      return true;
    }
    if (cfg.visible_by_source) {
      return !!cfg.visible_by_source[this.sourceType];
    }
    return false;
  }

  private buildSections(): void {
    this.sections = Object.entries(this.config.sections).map(([key, cfg]: [string, any]) => ({
      key, cfg,
      fields: cfg.fields
        ? Object.entries(cfg.fields).map(([fk, fc]) => ({ key: fk, cfg: fc as any }))
        : []
    }));
    this.sectionCfgs = {};
    this.sectionFieldConfigs = {};
    for (const section of this.sections) {
      this.sectionCfgs[section.key] = section.cfg;
      this.sectionFieldConfigs[section.key] = {};
      for (const field of section.fields) {
        this.sectionFieldConfigs[section.key][field.key] = field.cfg;
      }
    }
  }

  private buildForm(): void {
    const controls: Record<string, any> = {};
    for (const section of this.sections) {
      for (const field of section.fields) {
        controls[field.key] = [null];
      }
    }
    controls[PO_FIELD.MAKER_NAME] = [null];
    controls[PO_FIELD.BRAND_GROUP_TO_EXECUTE_NAME] = [null];
    controls[PO_FIELD.IS_TEST] = [false];
    // The PO document control holds an UploadedFileValue[] (multi-file) — never null.
    controls[PO_FIELD.DOCUMENT_FILE] = [[]];
    this.form = this.fb.group(controls);
  }

  private clearNonApplicableFields(): void {
    for (const section of this.sections) {
      if (!this.isSectionVisible(section.cfg)) {
        this._clearSectionFields(section);
      } else {
        section.fields.forEach(field => this._applyFieldVisibility(field));
      }
    }
  }

  private _clearSectionFields(section: SectionDef): void {
    for (const field of section.fields) {
      this.form.get(field.key)?.setValue(null, { emitEvent: false });
    }
  }

  private _applyFieldVisibility(field: FieldDef): void {
    const ctrl = this.form.get(field.key);
    if (!ctrl) {
      return;
    }
    if (!this.isFieldVisible(field.key, field.cfg)) {
      ctrl.setValue(null, { emitEvent: false });
    } else if (this.mode === DETAIL_MODE.EDIT && !this.isFieldEditable(field.cfg, field.key)) {
      // editable_by_source only disables fields in edit mode; in creation mode
      // all visible fields are editable.
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  }

  private _initLockedSourceType(): void {
    const isKam      = this.permissionsService.checkPermission(PO_PERMISSIONS.KAM_CREATOR);
    const isFinance  = this.permissionsService.checkPermission(PO_PERMISSIONS.FINANCE_APPROVER);
    const isBusiness = this.permissionsService.checkPermission(PO_PERMISSIONS.BUSINESS_APPROVER);
    if (isBusiness && !isKam && !isFinance) {
      this.lockedSourceType = SOURCE_TYPE.PAID_BY_RAPPI;
    }
  }

  private _clearNonEditableForSource(incomingSource: SourceType): void {
    // editable_by_source only applies in edit mode — in creation mode all fields are editable
    // and should never be cleared/disabled by this check.
    if (this.mode !== DETAIL_MODE.EDIT) {
      return;
    }
    for (const section of this.sections) {
      for (const field of section.fields) {
        this._updateFieldEditableForSource(field, incomingSource);
      }
    }
  }

  private _updateFieldEditableForSource(field: FieldDef, incomingSource: SourceType): void {
    const ctrl = this.form.get(field.key);
    if (!ctrl) {
      return;
    }
    // Expiration date is never editable once the PO has left Draft, for any source type — not
    // a per-source_type rule, so it can't be expressed via editable_by_source in the JSON config.
    if (field.key === PO_FIELD.EXPIRATION_DATE) {
      if (incomingSource === SOURCE_TYPE.TRATO && this.isDraftEdit) {
        ctrl.enable({ emitEvent: false });
      } else {
        ctrl.disable({ emitEvent: false });
      }
      return;
    }
    if (field.cfg.editable_by_source?.[incomingSource] === false) {
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  }

  private isSectionVisible(cfg: any): boolean {
    if (cfg.visible === true) {
      return true;
    }
    if (cfg.visible_by_source) {
      return !!cfg.visible_by_source[this.sourceType];
    }
    return false;
  }

  private isFieldVisible(fieldKey: string, cfg: any): boolean {
    if (fieldKey === PO_FIELD.TAX_PERCENTAGE) {
      return !!cfg.visible_by_source?.[this.sourceType]
        && !!(this.remoteConfig?.tax_percentage_options?.length);
    }
    if (fieldKey === PO_FIELD.TAGS) {
      return !!(this.tagsCatalog?.length);
    }
    if (cfg.visible === false) {
      return false;
    }
    if (cfg.visible === true) {
      return true;
    }
    if (cfg.visible_by_source) {
      return !!cfg.visible_by_source[this.sourceType];
    }
    if (cfg.visible_by_permission_and_source) {
      const excluded: string[] = cfg.visible_by_permission_and_source.excluded_sources ?? [];
      return !excluded.includes(this.sourceType);
    }
    if (cfg.visible_by_agency_field) {
      return !!this.form?.get(PO_FIELD.AGENCY)?.value
        && !!(this.remoteConfig?.agency_recognition_method_options?.length);
    }
    return true;
  }

  private _updateMandatoryValidators(sourceType: SourceType): void {
    for (const section of this.sections) {
      section.fields.forEach(field => this._updateFieldMandatoryValidator(field, sourceType));
    }
  }

  private _updateFieldMandatoryValidator(field: FieldDef, sourceType: SourceType): void {
    const cfg = field.cfg;
    // Expiration date is required only for Trato AND only while the CMS rollout flag is on — not
    // expressible via the JSON config's mandatory_by_source (which has no flag concept).
    if (field.key === PO_FIELD.EXPIRATION_DATE) {
      const ctrl = this.form.get(field.key);
      const shouldBeRequired = sourceType === SOURCE_TYPE.TRATO && this.tratoExpirationDateEnabled;
      if (shouldBeRequired) {
        ctrl?.setValidators([Validators.required]);
      } else {
        ctrl?.clearValidators();
      }
      ctrl?.updateValueAndValidity({ emitEvent: false });
      return;
    }
    if (!cfg.mandatory_by_source) {
      return;
    }
    const ctrl = this.form.get(field.key);
    if (!ctrl) {
      return;
    }
    // tax_percentage is required ONLY for Brazil + Trato. The config's mandatory_by_source
    // marks it for trato, but the country gate must be enforced here.
    const isTaxPercentage = field.key === PO_FIELD.TAX_PERCENTAGE;
    const shouldBeRequired = isTaxPercentage
      ? (this.storeAux.country === 'BR' && sourceType === SOURCE_TYPE.TRATO)
      : !!cfg.mandatory_by_source[sourceType];
    if (shouldBeRequired) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private isFieldEditable(cfg: any, fieldKey?: string): boolean {
    // Locked forever once the PO leaves Draft, regardless of source type/cfg — see
    // _clearNonEditableForSource for the initial-load/edit-mode counterpart of this same rule.
    if (fieldKey === PO_FIELD.EXPIRATION_DATE) {
      return this.isDraftEdit;
    }
    if (cfg.editable_by_source) {
      return cfg.editable_by_source[this.sourceType] !== false;
    }
    return cfg.editable !== false;
  }

  // one-datepicker binds the real FormControl straight to MatDatepicker (see
  // ngx-frontend/libs/ui/datepicker) — the control value is whatever the date adapter produces
  // (Date, possibly Moment), never a raw string. The backend expects/returns date-only
  // "YYYY-MM-DD" (no time/timezone). Both helpers use LOCAL getters/constructor — never
  // toISOString() or single-string `new Date(iso)` parsing — to avoid the UTC off-by-one-day bug.
  private _toDateOnlyString(value: unknown): string | undefined {
    if (!value) {
      return undefined;
    }
    // Number(value) coerces a Moment (or any valueOf()-bearing date-like object) to epoch ms without
    // an `any` cast; a stray primitive that can't coerce falls through to NaN and is safely dropped.
    const date = value instanceof Date ? value : new Date(Number(value));
    if (isNaN(date.getTime())) {
      return undefined;
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private _parseDateOnly(dateStr: string): Date | null {
    const [y, m, d] = dateStr.split('-').map(Number);
    return y && m && d ? new Date(y, m - 1, d) : null;
  }
}
